import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { PiArrowCounterClockwise } from 'react-icons/pi';

import { fetchRepairItemList } from '#/api/aftersalesMgmt/repairItem/fetchList';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import {
  dialogContentCompactGapClassName,
  dialogWideMaxWidthClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import TableSearchInput from '#/modules/commonComponents/table/TableSearchInput';
import { createSelectColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/modules/commonComponents/tooltip';
import {
  buildRepairItemPickerColumns,
  REPAIR_ITEM_PICKER_SORT_FIELD_LABELS,
} from '#/modules/crmMgmt/aftersales/signReceipt/data/repairItemPickerColumns';
import useRepairItemPickerListQuery from
  '#/modules/crmMgmt/aftersales/signReceipt/hooks/useRepairItemPickerListQuery';
import { repairItemListQueryKey } from '#/utils/queryKeys';

/**
 * 對齊現行 onConfirm shape（禁改 create API／表單契約）。
 */
const SIGN_RECEIPT_SELECTABLE_ITEM_STATUS = new Set(['REPAIRED', 'REFUSED']);

function isSelectableRepairItem(row) {
  return SIGN_RECEIPT_SELECTABLE_ITEM_STATUS.has(row?.status);
}

function mapRowToSelectedItem(row) {
  const id = row?.id ?? row?.repairItemId;
  if (!id) {
    return null;
  }
  return {
    id,
    repairItemId: id,
    serialNo: row.serialNo ?? '',
    productCode: row.productCode ?? '',
    itemName: row.itemName ?? '',
    problemDescription: row.problemDescription ?? '',
    accessories: row.accessories ?? '',
    repairRequestNo: row.repairRequestNo ?? '',
    status: row.status ?? '',
  };
}

function buildInitialSelection(initialSelectedItems = []) {
  const rowSelection = {};
  const selectedById = {};

  (initialSelectedItems ?? []).forEach((item) => {
    const mapped = mapRowToSelectedItem(item);
    if (!mapped) {
      return;
    }
    const id = String(mapped.id);
    rowSelection[id] = true;
    selectedById[id] = mapped;
  });

  return { rowSelection, selectedById };
}

/**
 * 簽收選維修品項：ListTablePanel 多選（禁 SN／report selector）。
 * 已選列可見＋預勾；確認前不改表單；確認＝全量同步。
 */
const RepairItemPickerDialog = ({
  open,
  onOpenChange,
  initialSelectedItems = [],
  onConfirm,
}) => {
  const {
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetQuery,
  } = useRepairItemPickerListQuery();

  const initialSelection = useMemo(
    () => buildInitialSelection(initialSelectedItems),
    [initialSelectedItems],
  );

  const [selectedById, setSelectedById] = useState(initialSelection.selectedById);
  const [tableMountKey, setTableMountKey] = useState(0);
  const [searchKeyword, setSearchKeyword] = useState('');

  const listQueryResult = useQuery({
    queryKey: repairItemListQueryKey(listQuery),
    queryFn: () => fetchRepairItemList(listQuery),
    enabled: open,
    placeholderData: keepPreviousData,
    retry: false,
  });

  const rows = useMemo(
    () => listQueryResult.data?.data ?? [],
    [listQueryResult.data?.data],
  );

  const handleFilterChange = useCallback((key, value) => {
    setFilter(key, value);
  }, [setFilter]);

  const columns = useMemo(() => ([
    createSelectColumn({
      enableMultiSelection: true,
      showHeaderCheckbox: true,
    }),
    ...buildRepairItemPickerColumns({
      filters: listQuery.filters,
      onFilterChange: handleFilterChange,
    }),
  ]), [listQuery.filters, handleFilterChange]);

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns),
    [columns],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const handleRowSelectionChange = useCallback((selection) => {
    setSelectedById((prev) => {
      const next = { ...prev };

      rows.forEach((row) => {
        const id = String(row.id);
        if (selection[id] && isSelectableRepairItem(row)) {
          const mapped = mapRowToSelectedItem(row);
          if (mapped) {
            next[id] = mapped;
          }
        } else {
          delete next[id];
        }
      });

      return next;
    });
  }, [rows]);

  function handleSearchSubmit() {
    setKeyword(searchKeyword.trim());
  }

  function handleResetFilters() {
    setSearchKeyword('');
    resetQuery();
  }

  function handleConfirm() {
    const selected = Object.values(selectedById)
      .filter((item) => item && (!item.status || isSelectableRepairItem(item)))
      .map((item) => {
        const next = { ...item };
        delete next.status;
        return next;
      });
    onConfirm(selected);
    onOpenChange(false);
  }

  function handleCancel() {
    onOpenChange(false);
  }

  function handleOpenChange(nextOpen) {
    if (nextOpen) {
      const next = buildInitialSelection(initialSelectedItems);
      setSelectedById(next.selectedById);
      setSearchKeyword('');
      resetQuery();
      setTableMountKey((prev) => prev + 1);
    } else {
      resetQuery();
      setSearchKeyword('');
    }
    onOpenChange(nextOpen);
  }

  const selectedCount = Object.keys(selectedById).length;

  const headerSearch = (
    <div className="flex w-full max-w-md items-center gap-2 sm:w-72">
      <TableSearchInput
        useKeyword
        keyword={searchKeyword}
        onKeywordChange={setSearchKeyword}
        onSearch={handleSearchSubmit}
        placeholder="搜尋序號、品名、報修單號或備註"
        className="min-w-0 w-full text-foreground"
      />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="secondary"
            size="icon"
            className={cn(
              'hover:text-gray-50',
              'size-10 shrink-0',
            )}
            aria-label="清除搜尋與篩選"
            onClick={handleResetFilters}
          >
            <PiArrowCounterClockwise size={20} aria-hidden />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">清除搜尋與篩選</TooltipContent>
      </Tooltip>
    </div>
  );

  return (
    <AppDialog
      open={open}
      onOpenChange={handleOpenChange}
      title="選擇維修品項"
      description="只可勾選已修好或拒修的維修品項；待處理、處理中與已送回不可選。"
      headerVariant="primary"
      headerEnd={headerSearch}
      showCloseButton={false}
      maxWidthClassName={dialogWideMaxWidthClassName}
      contentClassName={dialogContentCompactGapClassName}
      closeOnOutsideClick={false}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      confirmText={`確認選取（${selectedCount}）`}
      cancelText="取消"
      confirmDisabled={listQueryResult.isLoading}
    >
      <ListTablePanel
        key={tableMountKey}
        variant="plain"
        columns={columns}
        rows={rows}
        pagination={listQueryResult.data?.page ?? {
          current_page: 1,
          page_size: listQuery.pageSize,
          total_count: 0,
          total_page: 1,
        }}
        isLoading={listQueryResult.isLoading}
        isError={listQueryResult.isError}
        errorMessage="載入維修品項失敗"
        onRetry={() => listQueryResult.refetch()}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        listQuery={listQuery}
        filterStatusFields={filterStatusFields}
        sortFieldLabels={REPAIR_ITEM_PICKER_SORT_FIELD_LABELS}
        onFilterChange={setFilter}
        onKeywordClear={() => {
          setSearchKeyword('');
          setKeyword('');
        }}
        onSortRemove={removeSortAt}
        onSortingChange={handleSortingChange}
        enableMultiSelection
        paginationClassName="px-0 py-2"
        initialRowSelection={initialSelection.rowSelection}
        onRowSelectionChange={handleRowSelectionChange}
      />
    </AppDialog>
  );
};

RepairItemPickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  initialSelectedItems: PropTypes.arrayOf(PropTypes.object),
  onConfirm: PropTypes.func.isRequired,
};

export default RepairItemPickerDialog;
